/**
 * @file EventLogReportParameters.js
 * @description Component for selecting Event Log Report parameters before displaying results for the Generate Report component.
 */

import React, { useState, useEffect } from 'react';
import { subDays, format } from 'date-fns';
import { getLogEventSeverityType } from '../../utils/utils';
import { LogEventTypeSeverity } from '../../utils/data/enums';
import { searchIndividuals } from '../../services/individualService';
import { queryPortalPages } from '../../services/portalPageService';
import { queryContainers } from '../../services/containerService';
import {
    getEventTypes,
    getBrowsers,
    getDevices,
} from '../../services/loggingService';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import './EventLogReportParameters.scss';
import { getText } from '../../i18n';
// Defaults for the dates
const defaultToDate = new Date();
const defaultFromDate = subDays(defaultToDate, 7);

// Defaults for the dropdown options state
const initialDropdownOptions = {
    eventTypeOptions: [],
    severityOptions: [],
    individualOptions: [],
    browserOptions: [],
    deviceTypeOptions: [],
    portalOptions: [],
    containerOptions: [],
};

// Defaults for the dropdown errors, we only track error state for the dropdowns that perform API calls
const initialDropdownErrors = {
    eventTypeOptions: false,
    individualOptions: false,
    portalOptions: false,
    containerOptions: false,
    browserOptions: false,
    deviceTypeOptions: false,
};

// Default state for the Event Log List parameters
const initialEventLogParameters = {
    fromDate: format(defaultFromDate, 'yyyy-MM-dd'),
    toDate: format(defaultToDate, 'yyyy-MM-dd'),
    selectedEventTypes: [],
    selectedSeverities: [],
    selectedIndividuals: [],
    selectedBrowsers: [],
    selectedDeviceTypes: [],
    selectedPortals: [],
    selectedContainers: [],
};

function normalizeInitialEventLogParameters(initialParameters) {
    if (!initialParameters) {
        return initialEventLogParameters;
    }

    return {
        ...initialEventLogParameters,
        ...initialParameters,
        selectedEventTypes: initialParameters.selectedEventTypes || [],
        selectedSeverities: initialParameters.selectedSeverities || [],
        selectedIndividuals: initialParameters.selectedIndividuals || [],
        selectedBrowsers: initialParameters.selectedBrowsers || [],
        selectedDeviceTypes: initialParameters.selectedDeviceTypes || [],
        selectedPortals: initialParameters.selectedPortals || [],
        selectedContainers: initialParameters.selectedContainers || [],
    };
}

const EventLogReportParameters = ({
    initialParameters = null,
    readOnly = false,
    onParametersChange = null,
} = {}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [dropdownOptions, setDropdownOptions] = useState(
        initialDropdownOptions,
    );
    const [dropdownErrors, setDropdownErrors] = useState(initialDropdownErrors);
    const [eventLogParameters, setEventLogParameters] = useState(
        normalizeInitialEventLogParameters(initialParameters),
    );

    useEffect(() => {
        setEventLogParameters(
            normalizeInitialEventLogParameters(initialParameters),
        );
    }, [initialParameters]);

    useEffect(() => {
        if (typeof onParametersChange === 'function') {
            onParametersChange(eventLogParameters);
        }
    }, [eventLogParameters, onParametersChange]);

    // Load all data on initial render - sets the dropdown option state for event type and severity, and also queries and sets dropdown state for individuals, browsers, device types, portal pages, and containers
    useEffect(() => {
        const fetchAllDropdownData = async () => {
            setIsLoading(true);

            // Populate the Severity dropdown
            const severityOptions = Object.values(LogEventTypeSeverity).map(
                type => ({
                    id: type,
                    name: getLogEventSeverityType(type),
                }),
            );

            // State for the dropdown options that we use API calls to populate
            let eventTypeOptions = [];
            let individualOptions = [];
            let portalOptions = [];
            let containerOptions = [];
            let browserOptions = [];
            let deviceTypeOptions = [];
            const errors = { ...initialDropdownErrors };

            // Populate the Event Type dropdown
            try {
                const eventTypes = await getEventTypes();
                if (Array.isArray(eventTypes) && eventTypes.length > 0) {
                    eventTypeOptions = eventTypes
                        .filter(type => type != null)
                        .map(type => ({
                            id: type.id,
                            name: type.name,
                        }));
                    errors.eventTypeOptions = false;
                } else {
                    Toast.error(
                        getText('msg_8316e940b93c'),
                    );
                    errors.eventTypeOptions = true;
                }
            } catch (error) {
                Toast.error(
                    getText('msg_b70b1dd3296c'),
                );
                errors.eventTypeOptions = true;
            }

            // Populate the Individuals dropdown
            try {
                const { individuals } = await searchIndividuals(null);
                if (Array.isArray(individuals) && individuals.length > 0) {
                    individualOptions = individuals
                        .map(individual => ({
                            id: individual.id,
                            tenantUserId: individual.uniqueId,
                            name: `${individual.firstName} ${individual.lastName} (${individual.uniqueId})`,
                            firstName: individual.firstName,
                            lastName: individual.lastName,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    errors.individualOptions = false;
                } else {
                    Toast.error(
                        getText('msg_94b7503dc539'),
                    );
                    errors.individualOptions = true;
                }
            } catch (error) {
                Toast.error(
                    getText('msg_020e6376ee00'),
                );
                errors.individualOptions = true;
            }

            // Populate the Browser dropdown
            try {
                const browsers = await getBrowsers();
                if (Array.isArray(browsers) && browsers.length > 0) {
                    browserOptions = browsers
                        .filter(browser => browser != null)
                        .map((browser, index) => ({
                            id: index,
                            name: browser,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                } else {
                    Toast.error(
                        getText('msg_d5776cb1c5e8'),
                    );
                    errors.browserOptions = true;
                }
            } catch (error) {
                Toast.error(
                    getText('msg_a470ac011be1'),
                );
                errors.browserOptions = true;
            }

            // Populate the Device Type dropdown
            try {
                const deviceTypes = await getDevices();
                if (Array.isArray(deviceTypes) && deviceTypes.length > 0) {
                    deviceTypeOptions = deviceTypes
                        .filter(deviceType => deviceType != null)
                        .map((deviceType, index) => ({
                            id: index,
                            name:
                                deviceType === 'Other' ? 'Desktop' : deviceType, // Handles the case for Desktop as Event objects are logged as "Other" for the name
                            apiValue: deviceType,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                } else {
                    Toast.error(
                        getText('msg_c215811f2357'),
                    );
                    errors.deviceTypeOptions = true;
                }
            } catch (error) {
                Toast.error(
                    getText('msg_46c5848a43a8'),
                );
                errors.deviceTypeOptions = true;
            }

            // Populate the Portals dropdown
            try {
                const { viewModels } = await queryPortalPages(null);
                if (Array.isArray(viewModels) && viewModels.length > 0) {
                    portalOptions = viewModels
                        .map(page => ({
                            id: page.id,
                            name: `${page.name} (${page.id})`,
                            portalDescription: page.description,
                            portalName: page.name,
                            isChild: page.isChild,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    errors.portalOptions = false;
                } else {
                    Toast.warning(
                        getText('msg_947ce659dd55'),
                    );
                    errors.portalOptions = true;
                }
            } catch (error) {
                Toast.warning(
                    getText('msg_21a5647f3335'),
                );
                errors.portalOptions = true;
            }

            // Populate the Containers dropdown
            try {
                const containers = await queryContainers();
                if (Array.isArray(containers) && containers.length > 0) {
                    containerOptions = containers
                        .map(container => ({
                            id: container.id,
                            name: `${container.title} (${container.id})`,
                            containerName: container.name,
                            containerTitle: container.title,
                            type: container.type,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));
                    errors.containerOptions = false;
                } else {
                    Toast.error(
                        getText('msg_d88365de9686'),
                    );
                    errors.containerOptions = true;
                }
            } catch (error) {
                Toast.error(
                    getText('msg_6e6fafe1f625'),
                );
                errors.containerOptions = true;
            }

            setDropdownOptions({
                eventTypeOptions,
                severityOptions,
                individualOptions,
                browserOptions,
                deviceTypeOptions,
                portalOptions,
                containerOptions,
            });
            setDropdownErrors(errors);
            setIsLoading(false);
        };

        fetchAllDropdownData();
    }, [readOnly]);

    useEffect(() => {
        if (!readOnly || isLoading) {
            return;
        }

        function resolveByIds(rawValues, options) {
            if (!Array.isArray(rawValues) || rawValues.length === 0) {
                return [];
            }
            if (typeof rawValues[0] === 'object' && rawValues[0].name) {
                return rawValues;
            }
            const idSet = new Set(
                rawValues.map(v => String(typeof v === 'object' ? v.id : v)),
            );
            return options.filter(opt => idSet.has(String(opt.id)));
        }

        function resolveByNames(rawValues, options) {
            if (!Array.isArray(rawValues) || rawValues.length === 0) {
                return [];
            }
            if (typeof rawValues[0] === 'object' && rawValues[0].name) {
                return rawValues;
            }
            const nameSet = new Set(
                rawValues.map(v =>
                    String(typeof v === 'object' ? v.name || v.apiValue : v),
                ),
            );
            return options.filter(
                opt =>
                    nameSet.has(String(opt.name)) ||
                    nameSet.has(String(opt.apiValue || opt.name)),
            );
        }

        setEventLogParameters(prev => ({
            ...prev,
            selectedEventTypes: resolveByIds(
                prev.selectedEventTypes,
                dropdownOptions.eventTypeOptions,
            ),
            selectedSeverities: resolveByIds(
                prev.selectedSeverities,
                dropdownOptions.severityOptions,
            ),
            selectedIndividuals: resolveByIds(
                prev.selectedIndividuals,
                dropdownOptions.individualOptions,
            ),
            selectedBrowsers: resolveByNames(
                prev.selectedBrowsers,
                dropdownOptions.browserOptions,
            ),
            selectedDeviceTypes: resolveByNames(
                prev.selectedDeviceTypes,
                dropdownOptions.deviceTypeOptions,
            ),
            selectedPortals: resolveByIds(
                prev.selectedPortals,
                dropdownOptions.portalOptions,
            ),
            selectedContainers: resolveByIds(
                prev.selectedContainers,
                dropdownOptions.containerOptions,
            ),
        }));
    }, [readOnly, isLoading, dropdownOptions]);

    const handleFieldChange = (field, value) => {
        if (readOnly) {
            return;
        }

        setEventLogParameters(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    if (isLoading) {
        return <Spinner width={50} className="loadingSpinner" />;
    }

    return (
        <div className="event-log-form">
            <div className="form-row center-row">
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_2e1d8fd9f8a5')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedEventTypes.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedEventTypes.length} Selected`
                        }
                        options={dropdownOptions.eventTypeOptions}
                        optionIDField="id"
                        savedOptionIDField="id"
                        readOnly={readOnly}
                        showTags={readOnly}
                        updateOptions={options =>
                            handleFieldChange('selectedEventTypes', options)
                        }
                        savedOptions={eventLogParameters.selectedEventTypes}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.eventTypeOptions.length}
                        selectAll={true}
                    />
                </label>
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_de314fa0c9d9')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedSeverities.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedSeverities.length} Selected`
                        }
                        options={dropdownOptions.severityOptions}
                        optionIDField="id"
                        savedOptionIDField="id"
                        readOnly={readOnly}
                        showTags={readOnly}
                        updateOptions={options =>
                            handleFieldChange('selectedSeverities', options)
                        }
                        savedOptions={eventLogParameters.selectedSeverities}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.severityOptions.length}
                        selectAll={true}
                    />
                </label>
            </div>
            <div className="form-row center-row">
                <label className="individuals-label-wrapper">
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_a96e829dca13')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedIndividuals.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedIndividuals.length} Selected`
                        }
                        options={dropdownOptions.individualOptions}
                        readOnly={readOnly || dropdownErrors.individualOptions}
                        optionIDField="id"
                        savedOptionIDField="id"
                        updateOptions={options =>
                            handleFieldChange('selectedIndividuals', options)
                        }
                        savedOptions={eventLogParameters.selectedIndividuals}
                        showTags
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.individualOptions.length}
                        selectAll={true}
                    />
                </label>
            </div>
            <div className="form-row center-row">
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_54a2cf5e634d')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedBrowsers.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedBrowsers.length} Selected`
                        }
                        options={dropdownOptions.browserOptions}
                        readOnly={readOnly || dropdownErrors.browserOptions}
                        showTags={readOnly}
                        optionIDField="id"
                        savedOptionIDField="id"
                        updateOptions={options =>
                            handleFieldChange('selectedBrowsers', options)
                        }
                        savedOptions={eventLogParameters.selectedBrowsers}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.browserOptions.length}
                        selectAll={true}
                    />
                </label>
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_20d5df8b4ab5')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedDeviceTypes.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedDeviceTypes.length} Selected`
                        }
                        options={dropdownOptions.deviceTypeOptions}
                        readOnly={readOnly || dropdownErrors.deviceTypeOptions}
                        showTags={readOnly}
                        optionIDField="id"
                        savedOptionIDField="id"
                        updateOptions={options =>
                            handleFieldChange('selectedDeviceTypes', options)
                        }
                        savedOptions={eventLogParameters.selectedDeviceTypes}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.deviceTypeOptions.length}
                        selectAll={true}
                    />
                </label>
            </div>
            <div className="form-row center-row">
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_a0c88242e813')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedPortals.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedPortals.length} Selected`
                        }
                        options={dropdownOptions.portalOptions}
                        readOnly={readOnly || dropdownErrors.portalOptions}
                        showTags={readOnly}
                        optionIDField="id"
                        savedOptionIDField="id"
                        updateOptions={options =>
                            handleFieldChange('selectedPortals', options)
                        }
                        savedOptions={eventLogParameters.selectedPortals}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.portalOptions.length}
                        selectAll={true}
                    />
                </label>
                <label>
                    <span
                        style={
                            readOnly
                                ? { color: 'var(--iNGAGED-Neutral-Color2)' }
                                : { marginBottom: '11px' }
                        }
                    >
                        {getText('msg_e040a458f465')}
                    </span>
                    <SearchableDropdown
                        placeholderLabel={
                            eventLogParameters.selectedContainers.length === 0
                                ? 'Select'
                                : `${eventLogParameters.selectedContainers.length} Selected`
                        }
                        options={dropdownOptions.containerOptions}
                        readOnly={readOnly || dropdownErrors.containerOptions}
                        showTags={readOnly}
                        optionIDField="id"
                        savedOptionIDField="id"
                        updateOptions={options =>
                            handleFieldChange('selectedContainers', options)
                        }
                        savedOptions={eventLogParameters.selectedContainers}
                        showSelectionBox={false}
                        showSelectedCount
                        showSelectedItemsInField={false}
                        minSelections={0}
                        maxSelections={dropdownOptions.containerOptions.length}
                        selectAll={true}
                    />
                </label>
            </div>
        </div>
    );
};

export default EventLogReportParameters;
